// © 2024–2026 Jos Jozef Sytze van der Werf — Alle rechten voorbehouden.
import { useEffect, useState, useCallback } from 'react'
import { Baby, Plus, Trash2 } from 'lucide-react'
import { Card }    from '@/components/ui/Card'
import { Input }   from '@/components/ui/Input'
import { Button }  from '@/components/ui/Button'
import { useWizardStore, useAuthStore } from '@/store'
import { useOnboardingStore } from '@/store'
import { InfoBlock } from '@/components/ui/InfoBlock'
import { useAutosave } from '@/hooks/useAutosave'
import { loadChildren, saveChildren, loadPartner, type ChildrenData, type ChildEntry, type GuardianData, type PartnerData, EMPTY_GUARDIAN } from '@/services/wizard'
import { ClosingGuidance } from '@/components/process/ClosingGuidance'
import { loadTrustedPersons, type TrustedPersonConfig } from '@/services/trustedPerson'
import { ROUTES } from '@/lib/constants/routes'
import { useNavigate } from 'react-router-dom'
import type { SaveStatus } from '@/hooks/useAutosave'

// ── Helpers ────────────────────────────────────────────────────────────────────

function emptyChild(): ChildEntry {
  return { name: '', lastName: '', dob: '', relation: '', gender: '' }
}

function calcAge(dob: string): string {
  if (!dob || dob.length < 10) return ''
  try {
    const ms  = Date.now() - new Date(dob).getTime()
    const age = Math.floor(ms / (365.25 * 24 * 3600 * 1000))
    return age >= 0 && age < 120 ? `${age} jaar` : ''
  } catch { return '' }
}

function isMinor(dob: string): boolean {
  if (!dob || dob.length < 10) return false
  try {
    const age = Math.floor((Date.now() - new Date(dob).getTime()) / (365.25 * 24 * 3600 * 1000))
    return age >= 0 && age < 18
  } catch { return false }
}

// Drie dropdowns voor geboortedatum - betere mobiele UX dan native date picker
function buildDob(day: string, month: string, year: string): string {
  if (!day || !month || !year) return ''
  const d = day.padStart(2, '0')
  const m = month.padStart(2, '0')
  return `${year}-${m}-${d}`
}

function parseDob(dob: string): { day: string; month: string; year: string } {
  if (!dob || dob.length < 10) return { day: '', month: '', year: '' }
  const [year, month, day] = dob.split('-')
  return {
    day:   day   ? String(parseInt(day,   10)) : '',
    month: month ? String(parseInt(month, 10)) : '',
    year:  year  ?? '',
  }
}

const DAYS   = Array.from({ length: 31 }, (_, i) => String(i + 1))
const MONTHS = [
  { v: '1', l: 'januari' }, { v: '2', l: 'februari' }, { v: '3', l: 'maart' },
  { v: '4', l: 'april' },   { v: '5', l: 'mei' },       { v: '6', l: 'juni' },
  { v: '7', l: 'juli' },    { v: '8', l: 'augustus' },  { v: '9', l: 'september' },
  { v: '10', l: 'oktober' }, { v: '11', l: 'november' }, { v: '12', l: 'december' },
]
const YEARS = Array.from({ length: 100 }, (_, i) => String(new Date().getFullYear() - i))

// Geslacht knoppen
const GENDER_OPTIONS = [
  { value: 'jongen', label: 'Jongen' },
  { value: 'meisje', label: 'Meisje' },
  { value: 'anders', label: 'Anders' },
]

// ── DobPicker ─────────────────────────────────────────────────────────────────
// Opzet: de drie dropdown-waarden (day/month/year) worden opgeslagen als
// aparte dobDay/dobMonth/dobYear velden naast het gecombineerde 'dob' veld.
// Zo blijft de partieel ingevulde staat behouden na:
//   • autosave (600ms)
//   • navigatie Vorige/Volgende (component unmount + remount)
//   • herladen (uit Dexie hersteld)
// Als alle drie compleet zijn: dob = "YYYY-MM-DD", anders dob = "".

function DobPicker({
  day, month, year, onChange,
}: {
  day:      string
  month:    string
  year:     string
  onChange: (partial: { day: string; month: string; year: string; dob: string }) => void
}) {
  function commit(d: string, m: string, y: string) {
    const dob = buildDob(d, m, y)  // '' als onvolledig
    onChange({ day: d, month: m, year: y, dob })
  }

  return (
    <div>
      <p className="text-sm font-medium text-ink-2 mb-1.5">Geboortedatum</p>
      <div className="grid grid-cols-3 gap-2">
        {/* Dag */}
        <select
          value={day}
          onChange={e => commit(e.target.value, month, year)}
          className="rounded-xl border border-sand-300 bg-white px-3 py-2.5 text-sm text-ink focus:outline-none focus:ring-2 focus:ring-sage-400"
        >
          <option value="">Dag</option>
          {DAYS.map(d => <option key={d} value={d}>{d}</option>)}
        </select>
        {/* Maand */}
        <select
          value={month}
          onChange={e => commit(day, e.target.value, year)}
          className="rounded-xl border border-sand-300 bg-white px-3 py-2.5 text-sm text-ink focus:outline-none focus:ring-2 focus:ring-sage-400"
        >
          <option value="">Maand</option>
          {MONTHS.map(m => <option key={m.v} value={m.v}>{m.l}</option>)}
        </select>
        {/* Jaar */}
        <select
          value={year}
          onChange={e => commit(day, month, e.target.value)}
          className="rounded-xl border border-sand-300 bg-white px-3 py-2.5 text-sm text-ink focus:outline-none focus:ring-2 focus:ring-sage-400"
        >
          <option value="">Jaar</option>
          {YEARS.map(y => <option key={y} value={y}>{y}</option>)}
        </select>
      </div>
      {day && month && year && calcAge(buildDob(day, month, year)) && (
        <p className="text-xs text-sage-600 mt-1 font-medium">{calcAge(buildDob(day, month, year))}</p>
      )}
    </div>
  )
}

// ── Hoofdcomponent ─────────────────────────────────────────────────────────────

export function NewStep02Children({ onSaveStatus }: { onSaveStatus: (s: SaveStatus) => void }) {
  const { setAnswer } = useWizardStore()
  const { userId }    = useAuthStore()
  const navigate      = useNavigate()
  const [data,  setData]  = useState<ChildrenData>({ hasChildren: '', children: [], guardianNotes: '', guardian: EMPTY_GUARDIAN })
  const [ready, setReady] = useState(false)
  // Personen voor voogd-keuzehulp
  const [ctxPartner, setCtxPartner] = useState<PartnerData | null>(null)
  const [ctxVP,      setCtxVP]      = useState<TrustedPersonConfig[]>([])

  useEffect(() => {
    if (!userId) return
    loadChildren(userId).then(d => {
      if (d && (d.hasChildren !== '' || d.children.length > 0)) {
        // Dexie heeft bestaande data - gebruik die
        setData({ ...d, guardian: d.guardian ?? EMPTY_GUARDIAN })
      } else {
        // Dexie leeg - kijk of onboarding al kinderen heeft vastgelegd
        const onbData = useOnboardingStore.getState().data
        const hasKids = onbData.hasChildren === true
        const count   = hasKids ? (onbData.numberOfChildren ?? 1) : 0
        if (hasKids && count > 0) {
          // Pre-populeer met lege children-entries op basis van onboarding-aantal
          const prefilled: ChildrenData = {
            hasChildren:   'yes',
            children:      Array.from({ length: Math.min(count, 10) }, () => emptyChild()),
            guardianNotes: '',
            guardian:      EMPTY_GUARDIAN,
          }
          setData(prefilled)
          setAnswer('hasChildren', true)
        } else if (onbData.hasChildren === false) {
          setData({ hasChildren: 'no', children: [], guardianNotes: '', guardian: EMPTY_GUARDIAN })
          setAnswer('hasChildren', false)
        }
        // else: geen onboarding-info → start blanco
      }
      setReady(true)
    })
  }, [userId, setAnswer])

  // Laad partner + VP voor voogd-keuzehulp
  useEffect(() => {
    if (!userId) return
    loadPartner(userId).then(p => { if (p) setCtxPartner(p) })
    loadTrustedPersons(userId).then(vps => {
      setCtxVP(vps.filter(v => v.confirmationStatus !== 'REVOKED'))
    })
  }, [userId])

  const save = useCallback(async (d: ChildrenData) => {
    if (userId) await saveChildren(userId, d)
  }, [userId])
  const { status, saveNow } = useAutosave({ data, onSave: save, enabled: ready })
  useEffect(() => { onSaveStatus(status) }, [status, onSaveStatus])

  function setHasChildren(v: string) {
    const children = v === 'yes'
      ? (data.children.length > 0 ? data.children : [emptyChild()])
      : []
    const next = { ...data, hasChildren: v, children }
    setData(next)
    setAnswer('hasChildren', v === 'yes')
  }

  function upd(i: number, field: keyof ChildEntry, value: string) {
    setData(p => {
      const children = [...p.children]
      children[i] = { ...children[i], [field]: value }
      return { ...p, children }
    })
  }

  // Aparte handler voor DobPicker: slaat dag/maand/jaar EN gecombineerde dob op
  function updDob(i: number, partial: { day: string; month: string; year: string; dob: string }) {
    setData(p => {
      const children = [...p.children]
      children[i] = {
        ...children[i],
        dobDay:   partial.day,
        dobMonth: partial.month,
        dobYear:  partial.year,
        dob:      partial.dob,
      }
      return { ...p, children }
    })
  }

  function updGuardian(field: keyof GuardianData, value: string) {
    setData(p => ({ ...p, guardian: { ...p.guardian, [field]: value } }))
  }

  function addChild() {
    setData(p => ({ ...p, children: [...p.children, emptyChild()] }))
  }

  function removeChild(i: number) {
    setData(p => ({ ...p, children: p.children.filter((_, j) => j !== i) }))
  }

  const hasMinors = data.children.some(c => isMinor(c.dob))

  return (
    <div className="flex flex-col gap-5">
      <div>
        <h2 className="text-xl font-semibold text-ink mb-1">Kinderen</h2>
        <p className="text-sm text-ink-3">Zodat uw kinderen worden meegenomen in uw voorbereiding.</p>
      </div>

      <InfoBlock title="Waarom legt WilsApp uw kinderen vast?" variant="blue">
        <p>Geen ouder denkt hier graag over na. Maar als er niets is vastgelegd, beslist uiteindelijk de rechter wie voor de kinderen zorgt, en dat hoeft niet de persoon te zijn die u zelf zou kiezen.</p>
        <p>De gegevens die u hier invult komen op meerdere plekken terug: in het <strong>nabestaandendossier</strong> als contactpersonen voor uw naasten, in het <strong>notarisdossier</strong> bij het opmaken van een testament, en u kunt een kind als <strong>vertrouwenspersoon</strong> kiezen, wat makkelijker gaat als de gegevens al klaarstaan.</p>
        <p><strong>Voogdij:</strong> heeft u minderjarige kinderen, dan kunt u hier vastleggen wie de voogd wordt als beide ouders overlijden. Vraag die persoon dat liever vooraf; het gebeurt vaker dan u denkt dat iemand pas achteraf hoort dat hij was bedacht, en dan toch gaat twijfelen.</p>
                                      </InfoBlock>

      {/* Ja / Nee */}
      <div className="bg-white rounded-2xl border border-sand-300 p-5">
        <p className="text-base font-medium text-ink mb-4">Heeft u kinderen?</p>
        <div className="flex flex-col gap-2">
          {[{ value: 'yes', label: 'Ja' }, { value: 'no', label: 'Nee' }].map(opt => (
            <button key={opt.value} type="button" onClick={() => setHasChildren(opt.value)}
              className={`flex items-center gap-3 rounded-xl border-2 px-4 py-4 text-left transition-all ${
                data.hasChildren === opt.value
                  ? 'border-sage-600 bg-sage-50'
                  : 'border-sand-300 bg-white hover:border-sage-300'
              }`}>
              <div className={`h-5 w-5 rounded-full border-2 flex-shrink-0 ${
                data.hasChildren === opt.value ? 'border-sage-600 bg-sage-600' : 'border-sand-300'
              }`} />
              <span className="text-base font-semibold text-ink-2">{opt.label}</span>
            </button>
          ))}
        </div>
      </div>

      {data.hasChildren === 'yes' && (
        <div className="flex flex-col gap-4 animate-slide-down">

          {/* Kindkaarten - één per kind */}
          {data.children.map((child, i) => (
            <Card key={i}>
              {/* Kaart-header */}
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-sage-50">
                    <Baby className="h-4 w-4 text-sage-600" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-ink">
                      {child.name ? child.name : `Kind ${i + 1}`}
                    </p>
                    {child.dob && calcAge(child.dob) && (
                      <p className="text-xs text-sage-600 font-medium">{calcAge(child.dob)}</p>
                    )}
                  </div>
                </div>
                {data.children.length > 1 && (
                  <button onClick={() => removeChild(i)}
                    className="flex h-8 w-8 items-center justify-center rounded-lg text-ink-4 hover:bg-status-alertBg hover:text-status-alertText transition-colors">
                    <Trash2 className="h-4 w-4" />
                  </button>
                )}
              </div>

              <div className="flex flex-col gap-4">
                {/* Naam */}
                <div className="grid gap-3 sm:grid-cols-2">
                  <Input
                    label="Voornaam"
                    value={child.name}
                    onChange={e => upd(i, 'name', e.target.value)}
                    placeholder="Voornaam"
                    autoFocus={i === 0 && !child.name}
                  />
                  <Input
                    label="Achternaam"
                    value={child.lastName ?? ''}
                    onChange={e => upd(i, 'lastName', e.target.value)}
                    placeholder="Achternaam (optioneel)"
                  />
                </div>

                {/* Geslacht */}
                <div>
                  <p className="text-sm font-medium text-ink-2 mb-1.5">Geslacht</p>
                  <div className="flex gap-2">
                    {GENDER_OPTIONS.map(g => (
                      <button
                        key={g.value}
                        type="button"
                        onClick={() => upd(i, 'gender', g.value)}
                        className={`flex-1 rounded-xl border-2 py-2.5 text-sm font-medium transition-all ${
                          child.gender === g.value
                            ? 'border-sage-600 bg-sage-50 text-sage-800'
                            : 'border-sand-300 text-ink-3 hover:border-sage-300'
                        }`}
                      >
                        {g.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Geboortedatum - drie dropdowns */}
                <DobPicker
                  day={child.dobDay ?? parseDob(child.dob).day}
                  month={child.dobMonth ?? parseDob(child.dob).month}
                  year={child.dobYear ?? parseDob(child.dob).year}
                  onChange={partial => updDob(i, partial)}
                />

                {/* Minderjarig-badge */}
                {isMinor(child.dob) && (
                  <div className="flex items-center gap-2 rounded-lg bg-amber-50 border border-amber-200 px-3 py-2">
                    <span className="text-sm">⚠️</span>
                    <p className="text-xs text-amber-700 font-medium">Minderjarig - voogdij vastleggen aanbevolen</p>
                  </div>
                )}
              </div>
            </Card>
          ))}

          {/* Nog een kind toevoegen */}
          <Button variant="outline" onClick={addChild} icon={<Plus className="h-4 w-4" />}>
            Nog een kind toevoegen
          </Button>

          {/* Voogdijbanner + navigatieknop */}
          {hasMinors && (
            <div className="flex items-start gap-3 rounded-xl bg-amber-50 border border-amber-200 px-4 py-3">
              <span className="text-lg flex-shrink-0">⚠️</span>
              <div className="flex-1">
                <p className="text-sm font-semibold text-amber-900 mb-0.5">U heeft minderjarige kinderen</p>
                <p className="text-xs text-amber-700 leading-relaxed mb-2">
                  Wilt u vastleggen wie de voogd wordt als beide ouders overlijden?
                  Dit is een belangrijke stap die juridisch via een notaris wordt vastgelegd.
                </p>
                <a href="https://www.rechtspraak.nl/Onderwerpen/Paginas/Voogdij.aspx" target="_blank" rel="noopener noreferrer"
                   className="text-xs font-semibold text-amber-800 underline underline-offset-2 hover:text-amber-900 block mt-1">
                  Rechtspraak.nl - informatie over voogdij →
                </a>
                <button
                  type="button"
                  onClick={async () => { await saveNow(); navigate(ROUTES.NOTARIAL) }}
                  className="text-xs font-semibold text-amber-800 underline underline-offset-2 hover:text-amber-900"
                >
                  Naar Notariële wensen → Voogdij →
                </button>
              </div>
            </div>
          )}

          {/* Voogdij-notities */}
          {/* Gestructureerde voogdijsectie - alleen bij minderjarige kinderen */}
          {hasMinors && (
            <Card>
              <h3 className="text-sm font-semibold text-ink mb-3">Voogdij bij overlijden</h3>
              <p className="text-xs text-ink-4 mb-4 leading-relaxed">
                Wie zorgt voor uw minderjarige kinderen als beide ouders komen te overlijden?
              </p>

              {/* Ja / Nee / In testament */}
              <div className="flex flex-col gap-2 mb-4">
                {[
                  { value: 'yes',       label: 'Ja, ik heb een voogd aangewezen' },
                  { value: 'testament', label: 'Ja, opgenomen in mijn testament' },
                  { value: 'no',        label: 'Nee, nog niet geregeld' },
                ].map(opt => (
                  <button key={opt.value} type="button"
                    onClick={() => updGuardian('appointed', opt.value)}
                    className={`flex items-center gap-3 rounded-xl border-2 px-4 py-3 text-left transition-all ${
                      data.guardian.appointed === opt.value
                        ? 'border-sage-600 bg-sage-50'
                        : 'border-sand-300 hover:border-sage-300'
                    }`}>
                    <div className={`h-4 w-4 rounded-full border-2 flex-shrink-0 ${
                      data.guardian.appointed === opt.value ? 'border-sage-600 bg-sage-600' : 'border-sand-300'
                    }`} />
                    <span className="text-sm text-ink">{opt.label}</span>
                  </button>
                ))}
              </div>

              {/* Voogdgegevens bij 'yes' - met keuzehulp */}
              {data.guardian.appointed === 'yes' && (() => {
                const primaryVP = ctxVP.find(v => v.role === 'PRIMARY')
                const reserveVP = ctxVP.find(v => v.role === 'RESERVE')
                const hasPartner = ctxPartner?.hasPartner === 'yes' && !!ctxPartner.name
                const hasPicker  = hasPartner || !!primaryVP || !!reserveVP

                // Bepaal of vrije invoer zichtbaar is:
                // - altijd als geen picker beschikbaar
                // - als guardian.name al iets bevat dat niet van een picker komt
                // - expliciet 'other' gekozen (guardian.relation === '__other__')
                const knownSources = ['Partner', 'Vertrouwenspersoon', 'Tweede vertrouwenspersoon', 'Reserve-vertrouwenspersoon']
                const showFreeForm = !hasPicker
                  || data.guardian.relation === '__other__'
                  || (!!data.guardian.name && !knownSources.includes(data.guardian.relation))

                return (
                  <div className="flex flex-col gap-3 animate-slide-down border-t border-sand-200 pt-4">

                    {/* Voorkeursvoogd header - altijd tonen */}
                    <div>
                      <p className="text-sm font-semibold text-ink mb-1">Voorkeursvoogd</p>
                      {hasPicker && (
                        <p className="text-xs text-ink-4">Selecteer uit uw dossier of voer een nieuwe persoon in.</p>
                      )}
                    </div>

                    {/* Snelle keuze - alleen als er personen beschikbaar zijn */}
                    {hasPicker && (
                      <div className="flex flex-col gap-2">
                        <p className="text-xs font-semibold text-ink-4 uppercase tracking-wider">Suggesties uit uw dossier</p>

                        {hasPartner && (
                          <button type="button"
                            onClick={() => {
                              const full = [ctxPartner!.name, ctxPartner!.lastName].filter(Boolean).join(' ')
                              updGuardian('name',     full)
                              updGuardian('relation', 'Partner')
                              updGuardian('phone',    ctxPartner!.phone ?? '')
                              updGuardian('email',    ctxPartner!.email ?? '')
                            }}
                            className={`flex items-center gap-3 rounded-xl border-2 px-4 py-2.5 text-left transition-all ${
                              data.guardian.relation === 'Partner'
                                ? 'border-sage-600 bg-sage-50'
                                : 'border-sand-300 hover:border-sage-300'
                            }`}>
                            <div className={`h-4 w-4 rounded-full border-2 flex-shrink-0 ${
                              data.guardian.relation === 'Partner' ? 'border-sage-600 bg-sage-600' : 'border-sand-300'
                            }`} />
                            <div>
                              <p className="text-sm text-ink font-medium">
                                Partner - {[ctxPartner!.name, ctxPartner!.lastName].filter(Boolean).join(' ')}
                              </p>
                            </div>
                          </button>
                        )}

                        {primaryVP && (
                          <button type="button"
                            onClick={() => {
                              updGuardian('name',     primaryVP.firstName)
                              updGuardian('relation', 'Vertrouwenspersoon')
                              updGuardian('phone',    '')
                              updGuardian('email',    '')
                            }}
                            className={`flex items-center gap-3 rounded-xl border-2 px-4 py-2.5 text-left transition-all ${
                              data.guardian.relation === 'Vertrouwenspersoon'
                                ? 'border-sage-600 bg-sage-50'
                                : 'border-sand-300 hover:border-sage-300'
                            }`}>
                            <div className={`h-4 w-4 rounded-full border-2 flex-shrink-0 ${
                              data.guardian.relation === 'Vertrouwenspersoon' ? 'border-sage-600 bg-sage-600' : 'border-sand-300'
                            }`} />
                            <p className="text-sm text-ink font-medium">
                              Eerste vertrouwenspersoon - {primaryVP.firstName}
                            </p>
                          </button>
                        )}

                        {reserveVP && (
                          <button type="button"
                            onClick={() => {
                              updGuardian('name',     reserveVP.firstName)
                              updGuardian('relation', 'Tweede vertrouwenspersoon')
                              updGuardian('phone',    '')
                              updGuardian('email',    '')
                            }}
                            className={`flex items-center gap-3 rounded-xl border-2 px-4 py-2.5 text-left transition-all ${
                              (data.guardian.relation === 'Tweede vertrouwenspersoon' || data.guardian.relation === 'Reserve-vertrouwenspersoon')
                                ? 'border-sage-600 bg-sage-50'
                                : 'border-sand-300 hover:border-sage-300'
                            }`}>
                            <div className={`h-4 w-4 rounded-full border-2 flex-shrink-0 ${
                              (data.guardian.relation === 'Tweede vertrouwenspersoon' || data.guardian.relation === 'Reserve-vertrouwenspersoon') ? 'border-sage-600 bg-sage-600' : 'border-sand-300'
                            }`} />
                            <p className="text-sm text-ink font-medium">
                              Tweede vertrouwenspersoon - {reserveVP.firstName}
                            </p>
                          </button>
                        )}

                        <button type="button"
                          onClick={() => {
                            updGuardian('relation', '__other__')
                            updGuardian('name',  '')
                            updGuardian('phone', '')
                            updGuardian('email', '')
                          }}
                          className={`flex items-center gap-3 rounded-xl border-2 px-4 py-2.5 text-left transition-all ${
                            showFreeForm && data.guardian.relation === '__other__'
                              ? 'border-sage-600 bg-sage-50'
                              : 'border-sand-300 hover:border-sage-300'
                          }`}>
                          <div className={`h-4 w-4 rounded-full border-2 flex-shrink-0 ${
                            showFreeForm && data.guardian.relation === '__other__' ? 'border-sage-600 bg-sage-600' : 'border-sand-300'
                          }`} />
                          <p className="text-sm text-ink">Iemand anders invoeren</p>
                        </button>
                      </div>
                    )}

                    {/* Vrije invoervelden - label aanpassen afhankelijk van context */}
                    {showFreeForm && (
                      <>
                        {!hasPicker && (
                          <p className="text-xs text-ink-4">Voer de gegevens van de voogd in. U kunt dit later aanvullen.</p>
                        )}
                        <div className="grid gap-3 sm:grid-cols-2">
                          <Input label="Naam voogd" value={data.guardian.name}
                            onChange={e => updGuardian('name', e.target.value)}
                            placeholder="Voornaam Achternaam" autoFocus={data.guardian.relation === '__other__'} />
                          <Input label="Relatie" value={data.guardian.relation === '__other__' ? '' : data.guardian.relation}
                            onChange={e => updGuardian('relation', e.target.value)}
                            placeholder="Bijv: zus, neef, vriend" />
                        </div>
                        <div className="grid gap-3 sm:grid-cols-2">
                          <Input label="Telefoonnummer" type="tel" value={data.guardian.phone}
                            onChange={e => updGuardian('phone', e.target.value)}
                            placeholder="06-..." />
                          <Input label="E-mailadres" type="email" value={data.guardian.email}
                            onChange={e => updGuardian('email', e.target.value)}
                            placeholder="naam@mail.nl" />
                        </div>
                      </>
                    )}

                    {/* Tweede voogd (optioneel) */}
                    {(showFreeForm || (data.guardian.name && knownSources.includes(data.guardian.relation))) && (
                      <div className="flex flex-col gap-3 border-t border-sand-200 pt-3">
                        <p className="text-xs font-semibold text-ink-4 uppercase tracking-wider">Tweede voogd (optioneel)</p>
                        {(hasPartner || primaryVP || reserveVP) && (
                          <div className="flex flex-col gap-2">
                            <p className="text-xs text-ink-4">Kies uit uw dossier - of vul hieronder handmatig in.</p>
                            {hasPartner && (
                              <button type="button"
                                onClick={() => {
                                  updGuardian('reserveName', [ctxPartner!.name, ctxPartner!.lastName].filter(Boolean).join(' '))
                                  updGuardian('reserveRelation', 'Partner')
                                  updGuardian('reservePhone', ctxPartner!.phone ?? '')
                                }}
                                className="flex items-center gap-3 rounded-xl border-2 px-4 py-2.5 text-left transition-all border-sand-300 hover:border-sage-300">
                                <span className="text-sm text-ink">Partner - {[ctxPartner!.name, ctxPartner!.lastName].filter(Boolean).join(' ')}</span>
                              </button>
                            )}
                            {primaryVP && (
                              <button type="button"
                                onClick={() => { updGuardian('reserveName', primaryVP.firstName); updGuardian('reserveRelation', 'Vertrouwenspersoon') }}
                                className="flex items-center gap-3 rounded-xl border-2 px-4 py-2.5 text-left transition-all border-sand-300 hover:border-sage-300">
                                <span className="text-sm text-ink">Vertrouwenspersoon - {primaryVP.firstName}</span>
                              </button>
                            )}
                            {reserveVP && (
                              <button type="button"
                                onClick={() => { updGuardian('reserveName', reserveVP.firstName); updGuardian('reserveRelation', 'Tweede vertrouwenspersoon') }}
                                className="flex items-center gap-3 rounded-xl border-2 px-4 py-2.5 text-left transition-all border-sand-300 hover:border-sage-300">
                                <span className="text-sm text-ink">Tweede vertrouwenspersoon - {reserveVP.firstName}</span>
                              </button>
                            )}
                          </div>
                        )}
                        <div className="grid gap-3 sm:grid-cols-2">
                          <Input label="Naam" value={data.guardian.reserveName}
                            onChange={e => updGuardian('reserveName', e.target.value)}
                            placeholder="Voornaam Achternaam" />
                          <Input label="Relatie" value={data.guardian.reserveRelation}
                            onChange={e => updGuardian('reserveRelation', e.target.value)}
                            placeholder="Bijv: zus, neef" />
                        </div>
                        <div className="grid gap-3 sm:grid-cols-2">
                          <Input label="Telefoonnummer" type="tel" value={data.guardian.reservePhone}
                            onChange={e => updGuardian('reservePhone', e.target.value)}
                            placeholder="06-..." />
                          <Input label="E-mailadres" type="email" value={data.guardian.reserveEmail}
                            onChange={e => updGuardian('reserveEmail', e.target.value)}
                            placeholder="naam@mail.nl" />
                        </div>
                      </div>
                    )}

                    {/* Toelichting voor notaris */}
                    {data.guardian.appointed === 'yes' && (
                      <div className="border-t border-sand-200 pt-3">
                        <Input label="Toelichting voor notaris (optioneel)" value={data.guardian.notarialNotes}
                          onChange={e => updGuardian('notarialNotes', e.target.value)}
                          placeholder="Bijv: voogdij is besproken en afgestemd met betrokkene" />
                        <p className="text-xs text-ink-4 mt-2 leading-relaxed">
                          Deze gegevens worden opgeslagen in uw notarisdossier. U kunt dit dossier
                          gebruiken om uw wensen met een notaris te bespreken als u een testament
                          wilt laten opstellen.
                        </p>
                      </div>
                    )}

                    {/* Samenvatting bij picker-keuze */}
                    {!showFreeForm && knownSources.includes(data.guardian.relation) && data.guardian.name && (
                      <div className="rounded-xl bg-sage-50 border border-sage-100 px-4 py-3">
                        <p className="text-xs text-sage-700">
                          <strong>{data.guardian.name}</strong> is aangewezen als voogd.
                          {!data.guardian.phone && (
                            <span className="block mt-1 text-sage-600">
                              Telefoonnummer nog niet ingevuld - u kunt dit hieronder aanvullen.
                            </span>
                          )}
                        </p>
                        {!data.guardian.phone && (
                          <div className="mt-3 grid gap-3 sm:grid-cols-2">
                            <Input label="Telefoonnummer (optioneel)" type="tel" value={data.guardian.phone}
                              onChange={e => updGuardian('phone', e.target.value)}
                              placeholder="06-..." />
                            <Input label="E-mailadres (optioneel)" type="email" value={data.guardian.email}
                              onChange={e => updGuardian('email', e.target.value)}
                              placeholder="naam@mail.nl" />
                          </div>
                        )}
                      </div>
                    )}

                  </div>
                )
              })()}

              {data.guardian.appointed === 'testament' && (
                <div className="flex flex-col gap-3 animate-slide-down">
                  <div className="rounded-xl bg-sage-50 border border-sage-200 px-4 py-3">
                    <p className="text-xs text-sage-700 leading-relaxed">
                      Voogdij is opgenomen in uw testament. Leg hieronder de notarisgegevens vast
                      zodat nabestaanden het testament kunnen raadplegen.
                    </p>
                  </div>
                  <div className="grid gap-3 sm:grid-cols-2">
                    <Input label="Notaris of kantoor" value={data.guardian.testamentNotaris}
                      onChange={e => updGuardian('testamentNotaris', e.target.value)}
                      placeholder="Bijv: Notariskantoor De Vries" />
                    <Input label="Jaar testament" value={data.guardian.testamentYear}
                      onChange={e => updGuardian('testamentYear', e.target.value)}
                      placeholder="Bijv: 2022" />
                  </div>
                  <Input label="Locatie document" value={data.guardian.testamentLocation}
                    onChange={e => updGuardian('testamentLocation', e.target.value)}
                    placeholder="Bijv: notariskantoor + kopie thuis in rode map" />
                </div>
              )}

              {data.guardian.appointed === 'no' && (
                <div className="rounded-xl bg-amber-50 border border-amber-200 px-4 py-3 animate-slide-down">
                  <p className="text-xs font-semibold text-amber-800 mb-1">Aandachtspunt voor uw notarisdossier</p>
                  <p className="text-xs text-amber-700 leading-relaxed">
                    Zonder voogdijregeling beslist de rechter wie de voogd wordt als beide ouders overlijden.
                    Dit wordt opgenomen als open punt in uw notarisdossier. Overleg met een notaris om
                    voogdij via uw testament vast te leggen.
                  </p>
                </div>
              )}
            </Card>
          )}
        </div>
      )}
      {data.children.length > 0 && (
        <ClosingGuidance title="Uw wensen voor uw kinderen zijn vastgelegd">
          Controleer deze gegevens wanneer uw gezinssituatie verandert.
        </ClosingGuidance>
      )}
    </div>
  )
}
